import { useState, useEffect, useRef, useCallback } from 'react';
import api from 'api';
import { toast } from 'react-toastify';
import { useParams, useHistory } from 'react-router-dom';
import '../assets/css/auth.css';

const OTP_LENGTH = 6;

const VerifyOtp = () => {
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);
  const param = useParams();
  const history = useHistory();

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return;
    }
    const timer = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft]);

  // Auto-focus first input
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const submitCode = useCallback((code) => {
    if (!param.email) {
      toast.error('No email provided.');
      return;
    }
    const trimmed = code.trim();
    if (!trimmed || isNaN(trimmed) || trimmed.length !== OTP_LENGTH) {
      toast.error('Please enter the full 6-digit code.');
      return;
    }
    setLoading(true);
    api('post', '/auth/verify-code', { email: param.email, code: parseInt(trimmed, 10) })
      .then((res) => {
        const { user } = res;
        localStorage.setItem('token', res.token);
        // Persist server-signed trusted-device token so next login on this
        // browser skips OTP for 30 days (replaces the old unsigned cookie).
        if (res.trustedDeviceToken) {
          localStorage.setItem('trustedDeviceToken', res.trustedDeviceToken);
        }
        if (user?.role === 'admin') {
          window.location = '/admin/index';
        } else if (user?.role === 'brand') {
          window.location = '/brand/index';
        }
      })
      .catch(() => {
        setLoading(false);
        toast.error('Invalid code. Please try again.');
        // Clear and refocus
        setDigits(Array(OTP_LENGTH).fill(''));
        if (inputRefs.current[0]) inputRefs.current[0].focus();
      });
  }, [param.email]);

  const handleChange = (index, value) => {
    // Handle paste of full code
    if (value.length > 1) {
      const pasted = value.replace(/\D/g, '').slice(0, OTP_LENGTH).split('');
      const newDigits = [...digits];
      pasted.forEach((d, i) => {
        if (index + i < OTP_LENGTH) newDigits[index + i] = d;
      });
      setDigits(newDigits);
      const nextIndex = Math.min(index + pasted.length, OTP_LENGTH - 1);
      if (inputRefs.current[nextIndex]) inputRefs.current[nextIndex].focus();
      // Auto-submit if full
      if (newDigits.every((d) => d !== '')) {
        submitCode(newDigits.join(''));
      }
      return;
    }

    // Single digit
    const digit = value.replace(/\D/g, '');
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1].focus();
    }

    // Auto-submit when complete
    if (digit && newDigits.every((d) => d !== '')) {
      submitCode(newDigits.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      const newDigits = [...digits];
      newDigits[index - 1] = '';
      setDigits(newDigits);
      inputRefs.current[index - 1].focus();
    }
    if (e.key === 'Enter') {
      submitCode(digits.join(''));
    }
  };

  const handleResend = () => {
    if (!param.email) {
      toast.error('No email provided.');
      return;
    }
    setLoading(true);
    api('post', '/auth/resendotp', { email: param.email })
      .then((res) => {
        setLoading(false);
        setCanResend(false);
        setTimeLeft(60);
        toast.success(res?.message || 'Code resent!');
      })
      .catch(() => {
        setLoading(false);
        toast.error('Failed to resend. Please try again.');
      });
  };

  const formatTime = (s) => {
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const maskedEmail = param.email
    ? param.email.replace(/(.{2})(.*)(@.*)/, (_, a, b, c) => a + '*'.repeat(Math.min(b.length, 5)) + c)
    : '';

  return (
    <div className="auth-card">
      <div className="auth-card__header">
        <div className="auth-card__logo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <h1 className="auth-card__title">Check your email</h1>
        <p className="auth-card__subtitle">
          We sent a verification code to<br />
          <strong style={{ color: '#32325d' }}>{maskedEmail}</strong>
        </p>
      </div>

      <div className="auth-card__body">
        <div className="auth-otp-group">
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              className="auth-otp-input"
              type="text"
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              maxLength={i === 0 ? OTP_LENGTH : 1}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              disabled={loading}
              aria-label={`Digit ${i + 1}`}
            />
          ))}
        </div>

        <div className="auth-resend">
          {canResend ? (
            <button
              type="button"
              className="auth-resend__btn"
              onClick={handleResend}
              disabled={loading}
            >
              Resend code
            </button>
          ) : (
            <span className="auth-resend__timer">
              Resend in {formatTime(timeLeft)}
            </span>
          )}
        </div>

        <button
          type="button"
          className="auth-btn auth-btn--primary"
          disabled={loading || digits.some((d) => !d)}
          onClick={() => submitCode(digits.join(''))}
        >
          {loading ? <span className="auth-btn__spinner" /> : 'Verify'}
        </button>

        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button
            type="button"
            className="auth-forgot"
            onClick={() => history.push('/auth/login')}
            style={{ fontSize: 13 }}
          >
            Back to sign in
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyOtp;
